import { Injectable, inject, signal } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { AuthService } from './auth.service';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { User } from '../models/core/user.model';
import { SelectionStateService } from './selection-state.service';
import { EventSellerService } from './event/event-seller.service';
import { ProductService } from './product/product.service';
import { DashboardSellerService } from './dashboard-seller/dashboard-seller.service';
import { SoldTicketsService } from './multi-event-tickets/sold-tickets.service';
import { BirthdayService } from './birthday/birthday.service';
import { PushService } from './push.service';
import { FirebaseMessagingService } from './firebase-messaging.service';
import { clearServerDeviceId } from '../utils/push-device.utils';
import { TurnstileWebSocketService } from './turnstile/turnstile-websocket.service';
import { GlobalReportService } from './sales-manager/global-report/global-report.service';
import { HeaderKpisService } from './sales-manager/global-report/header-kpis.service';
import { SellerReportService } from './sales-manager/global-report/seller-report.service';
import { ProductReportService } from './sales-manager/global-report/product-report.service';
import { ClientReportService } from './sales-manager/global-report/client-report.service';
import { EventAccessService } from './event-access/event-access.service';
import { SalesByLocationService } from './sales-manager/global-report/sales-by-location.service';
import { SalesByProductService } from './sales-manager/global-report/sales-by-product.service';
import { SalesByCategoryService } from './sales-manager/global-report/sales-by-category.service';
import { SalesTicketsService } from './sales-manager/global-report/sales-tickets.service';
import { RankedBreakdownService } from './sales-manager/global-report/ranked-breakdown.service';

@Injectable({
  providedIn: 'root'
})

export class MenuService {

  public userSignal = signal<User | undefined>(undefined);
  public isLoggingOut = false;

  private readonly _destroy$ = new Subject<void>();
  /** Cachés de reportes por `rol|evento` (sin usuario): se vacían al salir. */
  private readonly salesByLocationService = inject(SalesByLocationService);
  private readonly salesByProductService = inject(SalesByProductService);
  private readonly salesByCategoryService = inject(SalesByCategoryService);
  private readonly salesTicketsService = inject(SalesTicketsService);
  private readonly rankedBreakdownService = inject(RankedBreakdownService);

  constructor(
    private readonly _alertCtrl: AlertController,
    private readonly _translate: TranslateService,
    private readonly _logger: NGXLogger,
    private readonly _auth: AuthService,
    private readonly _router: Router,
    private readonly selectionState: SelectionStateService,
    private readonly eventSellerService: EventSellerService,
    private readonly productService: ProductService,
    private readonly dashboardSellerService: DashboardSellerService,
    private readonly soldTicketsService: SoldTicketsService,
    private readonly birthdayService: BirthdayService,
    private readonly pushService: PushService,
    private readonly firebaseMessagingService: FirebaseMessagingService,
    private readonly turnstileWebSocketService: TurnstileWebSocketService,
    private readonly globalReportService: GlobalReportService,
    private readonly headerKpisService: HeaderKpisService,
    private readonly sellerReportService: SellerReportService,
    private readonly productReportService: ProductReportService,
    private readonly clientReportService: ClientReportService,
    private readonly eventAccessService: EventAccessService,
  ) {
    this.trackNavigation();
  }

  private trackNavigation() {
    this._router.events.pipe(
      takeUntil(this._destroy$)
    ).subscribe(event => {
      if (event instanceof NavigationEnd) {
        const currentUrl = event.url;
        const isLoginRoute = currentUrl === '/login' || currentUrl === '/' || currentUrl.startsWith('/login');

        if (isLoginRoute) {
          this.clearMenuState();
        } else {
          this.checkRouteAndInitialize();
        }
      }
    });
  }

  private checkRouteAndInitialize() {
    if (this._router.url !== '/login') {
      this.getUserData();
    }
  }

  async getUserData() {
    const userData = await this._auth.getLogin();
    if (userData) {
      this.userSignal.set(userData.user);
      this._logger.info(`Current user: ${this.userSignal()?.fullname}`, this.userSignal());
    }
  }

  async logout() {
    const alert = await this._alertCtrl.create({
      header: this._translate.instant("logout.confirm.title"),
      message: this._translate.instant("logout.confirm.message"),
      mode: 'ios',
      cssClass: 'custom-logout-alert',
      buttons: [
        {
          text: this._translate.instant("cancel"),
          role: 'cancel',
          cssClass: 'secondary'
        },
        {
          text: this._translate.instant("confirm"),
          handler: async () => {
            if (this.isLoggingOut) return;

            this.isLoggingOut = true;

            const confirmButton = alert.querySelector('.alert-button:last-child') as HTMLButtonElement;
            if (confirmButton) {
              confirmButton.textContent = 'Espere...';
              confirmButton.disabled = true;
            }

            try {
              await this.selectionState.clear();

              this.eventSellerService.resetAllState();
              this.productService.resetAllState();
              this.dashboardSellerService.resetAllState();
              this.soldTicketsService.reset();
              this.birthdayService.resetAllState();

              this.globalReportService.resetAllState();
              this.headerKpisService.resetAllState();
              this.sellerReportService.resetAllState();
              this.productReportService.resetAllState();
              this.clientReportService.resetAllState();
              this.salesByLocationService.reset();
              this.salesByProductService.reset();
              this.salesByCategoryService.reset();
              this.salesTicketsService.reset();
              this.rankedBreakdownService.resetAllState();
              this.eventAccessService.reset();

              await this.pushService.deactivateCurrentDevice();
              await this.firebaseMessagingService.unsubscribeAllTopicsOnLogout();
              await clearServerDeviceId();

              this.turnstileWebSocketService.disconnect();

              await this._auth.logout();
              this._router.navigate(['/login']);
            } catch (error) {
              console.error('Error durante el logout:', error);
            } finally {
              this.isLoggingOut = false;
            }
          }
        }
      ]
    });

    await alert.present();
  }

  clearMenuState() {
    this.userSignal.set(undefined);
  }

}
